import { useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Header } from '@/components/layout/header';
import { Main } from '@/components/layout/main';
import { TopNav } from '@/components/layout/top-nav';
import { ProfileDropdown } from '@/components/profile-dropdown';
import { Search } from '@/components/search';
import { ThemeSwitch } from '@/components/theme-switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Route } from '@/routes/_authenticated/bolumler/bolumDetay/$bolumId/index';
import { Mail, Phone, School, Landmark, BookOpen, ClipboardList } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import apiClient from '@/lib/axios';

interface Egitmen {
  _id: string;
  adSoyad: string;
  mezuniyet: string;
  eposta: string;
  telefon: string;
  profilLink: string;
  profilResimUrl: string;
  unvan: string;
}

interface Bolum {
  _id: string;
  ad: string;
  dersSayisi: number;
  ogrenciSayisi: number;
  egitmenId: Egitmen;
}

interface Ders {
  _id: string;
  ad: string;
  bolumId: Array<{
    _id: string;
    ad: string;
  }>;
}

const topNav = [
  {
    title: "Ana Sayfa",
    href: "/",
    isActive: false,
  },
  {
    title: "Bölümler",
    href: "/bolumler",
    isActive: false,
  },
  {
    title: "Eğitmenler",
    href: "/egitmenler",
    isActive: false,
  },
];

export default function BolumDetayPage() {
  const { bolumId } = useParams({ from: Route.id });
  const [bolum, setBolum] = useState<Bolum | null>(null);
  const [dersler, setDersler] = useState<Ders[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  
  const getImageUrl = (profilResimUrl: string) => {
    return profilResimUrl || '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsImageLoading(true); // Reset image loading state when fetching new data
        const [bolumResponse, derslerResponse] = await Promise.all([
          apiClient.get(`/bolum/${bolumId}`),
          apiClient.get(`/ders/bolum/${bolumId}`)
        ]);
        
        setBolum(bolumResponse.data);
        setDersler(derslerResponse.data);
      } catch (err) {
        setError('Bölüm bilgileri yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    
    if (bolumId) {
      fetchData();
    }
  }, [bolumId]);

  if (loading) {
  return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center space-x-4">
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
    </div>
        </Header>
        <Main>
          <div className="mb-6">
            <Skeleton className="h-8 w-64 mb-2" />
          </div>
          <Tabs defaultValue="dersler">
            <TabsList>
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-10 w-48" />
            </TabsList>
            <TabsContent value="dersler">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-6 w-3/4" />
                    </CardHeader>
                    <CardFooter className="flex flex-col md:flex-col [1154px]:flex-row gap-2">
                      <Skeleton className="h-9 w-full" />
                      <Skeleton className="h-9 w-full" />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </Main>
      </>
    );
  }

  if (error || !bolum) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center space-x-4">
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
        </div>
        </Header>
        <Main>
          <div className="text-center py-8 text-red-500">{error || 'Bölüm Bulunamadı!'}</div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center space-x-4">
          <Search />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{bolum.ad}</h1>
        </div>
        <Tabs defaultValue="dersler">
          <TabsList>
            <TabsTrigger value="dersler" className="font-semibold text-gray-700">Dersler</TabsTrigger>
            <TabsTrigger value="egitimGorevlisi" className="font-semibold text-gray-700">Öğretim Görevlisi</TabsTrigger>
          </TabsList>

          {/* Dersler Tabı */}
          <TabsContent value="dersler">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dersler.map((ders) => (
                <Card
                  key={ders._id}
                >
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg font-semibold break-words">{ders.ad}</CardTitle>
                  </CardHeader>
                  <CardFooter className="flex flex-col md:flex-col [1154px]:flex-row gap-2 md:gap-2 [1154px]:gap-4">
                    <Link
                      to="/bolumler/bolumDetay/$bolumId/dersNotlari/$dersId"
                      params={{ bolumId, dersId: ders._id }}
                      className="w-full"
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                      >
                        <BookOpen className="h-4 w-4" />
                        <span className="hidden sm:inline">Ders Notları</span>
                        <span className="sm:hidden">Notlar</span>
                      </Button>
                    </Link>

                    <Link
                      to="/bolumler/bolumDetay/$bolumId/sinavlar/$dersId"
                      params={{ bolumId, dersId: ders._id }}
                      className="w-full"
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                      >
                        <ClipboardList className="h-4 w-4" />
                        Sınavlar
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Öğretim Görevlisi Tabı */}
          <TabsContent value="egitimGorevlisi">
            {bolum.egitmenId && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="relative">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {isImageLoading && (
                      <Skeleton className="h-20 w-20 shrink-0 rounded-full" />
                    )}
                    <Avatar className={`h-20 w-20 shrink-0 ${isImageLoading ? 'hidden' : ''}`}>
                      <AvatarImage 
                        src={getImageUrl(bolum.egitmenId.profilResimUrl)} 
                        alt={bolum.egitmenId.adSoyad}
                      />
                      <AvatarFallback>{bolum.egitmenId.adSoyad.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    {/* Preload image to detect when it's loaded */}
                    {isImageLoading && bolum.egitmenId.profilResimUrl && (
                      <img
                        src={getImageUrl(bolum.egitmenId.profilResimUrl)}
                        alt=""
                        className="hidden"
                        onLoad={() => setIsImageLoading(false)}
                        onError={() => setIsImageLoading(false)}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg sm:text-xl break-words">{bolum.egitmenId.adSoyad}</CardTitle>
                      <CardDescription className="break-words">{bolum.egitmenId.unvan}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-start sm:items-center space-x-2">
                      <Landmark className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                      <span className="text-muted-foreground break-words min-w-0">Pazaryeri Meslek Yüksekokulu</span>
                    </div>
                    <div className="flex items-start sm:items-center space-x-2">
                      <School className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                      <span className="text-muted-foreground break-words min-w-0">{bolum.egitmenId.mezuniyet}</span>
                    </div>
                    <div className="flex items-start sm:items-center space-x-2">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                      <a
                        href={`mailto:${bolum.egitmenId.eposta}`}
                        className="text-muted-foreground hover:text-primary transition-colors break-words min-w-0"
                      >
                        {bolum.egitmenId.eposta}
                      </a>
                    </div>
                    <div className="flex items-start sm:items-center space-x-2">
                      <Phone className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                      <a
                        href={`tel:${bolum.egitmenId.telefon.replace(/\s+/g, '')}`}
                        className="text-muted-foreground hover:text-primary transition-colors break-words min-w-0"
                      >
                        {bolum.egitmenId.telefon}
                      </a>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <a
                      href={bolum.egitmenId.profilLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button variant="outline" className="w-full">
                      Profili Görüntüle
                    </Button>
                  </a>
                </CardFooter>
              </Card>
            </div>
            )}
          </TabsContent>
        </Tabs>
      </Main>
    </>
  );
}
